(ns ui.view.kanban-opensearch.sub
  (:require-macros [ui.subs.macros :refer [<-1]])
  (:require
   [common.schema.registry :as registry]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [ui.view.kanban-opensearch.model :as model]))

(def ^:private draggable-grouping-data-types
  #{:data.type/boolean
    :data.type/keyword
    :data.type/ref-single})

(def ^:private non-draggable-grouping-fields
  #{:screening-profile/date-of-birth
    :common/created-on
    :common/updated-on})

(defn- resolve-entity-attribute-by-name
  [entity-type field-name]
  (when (and entity-type (string? field-name) (seq field-name))
    (let [entity-schema (registry/get-schema @session/tenant-schema entity-type)
          resolved (->> (keys entity-schema)
                        (filter keyword?)
                        (some (fn [attribute]
                                (when (= (name attribute) field-name)
                                  attribute))))]
      (when-not resolved
        (logging/error "Failed to resolve non-namespaced group-by-field leaf to entity attribute"
                       {:entity-type entity-type
                        :field-name field-name}))
      resolved)))

(defn- normalize-leaf-keyword
  [leaf entity-type field-path]
  (cond
    (nil? leaf)
    (if (and (nil? field-path)
             (nil? entity-type))
      nil
      (do (logging/error "Kanban group-by-field has invalid shape"
                         {:field-path field-path
                          :entity-type entity-type})
          nil))

    (not (keyword? leaf))
    (do (logging/error "Kanban group-by-field has invalid shape"
                       {:field-path field-path
                        :entity-type entity-type})
        nil)

    (namespace leaf)
    leaf

    :else
    (do (logging/warn "Kanban group-by-field leaf keyword is missing namespace"
                      {:field-path field-path
                       :leaf leaf
                       :entity-type entity-type})
        (or (resolve-entity-attribute-by-name entity-type (name leaf))
            (do (logging/error "Kanban group-by-field leaf keyword is missing namespace and cannot be resolved"
                               {:field-path field-path
                                :leaf leaf
                                :entity-type entity-type})
                nil)))))

(defn- leaf-field
  [field-path entity-type]
  (let [unwrap-singleton-seq (fn [v]
                               (if (and (sequential? v)
                                        (= 1 (count v))
                                        (sequential? (first v)))
                                 (do
                                   (logging/warn "Kanban group-by-field had nested singleton path; unwrapping"
                                                 {:field-path field-path
                                                  :unwrapped (first v)})
                                   (first v))
                                 v))
        normalized-path (unwrap-singleton-seq field-path)
        leaf (cond
               (keyword? normalized-path) normalized-path
               (sequential? normalized-path) (last normalized-path)
               :else nil)]
    (normalize-leaf-keyword leaf entity-type field-path)))

(rf/reg-sub
 ::kanban-state
 (fn [db [_ kanban-id]]
   (get-in db (model/kanban-path kanban-id))))

(rf/reg-sub
 ::config
 (<-1 ::kanban-state)
 (fn [kanban-state _]
   (get kanban-state model/config-key)))

(rf/reg-sub
 ::compiled-config
 (<-1 ::kanban-state)
 (fn [kanban-state _]
   (get kanban-state model/compiled-config-key)))

(rf/reg-sub
 ::entity-type
 (<-1 ::config)
 (fn [config _]
   (get config :entity-type)))

(rf/reg-sub
 ::group-by-field
 (<-1 ::config)
 (fn [config _]
   (get config :group-by-field)))

(rf/reg-sub
 ::reloading-values
 (fn [db [_ kanban-id]]
   (get-in db (into (model/kanban-path kanban-id) [:viewport :reloading-values]) #{})))

(rf/reg-sub
 ::grouping-draggable-debug
 (fn [[_ kanban-id] _]
   (rf/subscribe [::config kanban-id]))
 (fn [config [_ _kanban-id]]
   (let [group-by-field (:group-by-field config)
         row-group-by-field (:row-group-by-field config)
         entity-type (:entity-type config)
         pipeline-id (:pipeline-id config)
         attribute (leaf-field group-by-field entity-type)
         field-schema (when attribute
                        (registry/get-field-schema @session/tenant-schema attribute))
         data-type (:field/data-type field-schema)
         blocked-by-field? (contains? non-draggable-grouping-fields attribute)
         allowed-by-type? (contains? draggable-grouping-data-types data-type)
         draggable? (and (not blocked-by-field?)
                         allowed-by-type?)
         reason (cond
                  (nil? group-by-field) :missing-group-by-field
                  (nil? attribute) :missing-attribute
                  (nil? data-type) :missing-data-type
                  blocked-by-field? :blocked-by-field
                  (not allowed-by-type?) :unsupported-data-type
                  :else :ok)]
     #_(logging/info "KANBAN DND TRACE grouping data-type resolution"
                     {:kanban-id kanban-id
                      :config-exists? (boolean config)
                      :config-type (str (type config))
                      :config-keys (when (map? config) (-> config keys vec))
                      :config-pr (pr-str config)
                      :group-by-field group-by-field
                      :group-by-field-pr (pr-str group-by-field)
                      :group-by-field-type (str (type group-by-field))
                      :entity-type entity-type
                      :entity-type-type (str (type entity-type))
                      :pipeline-id pipeline-id
                      :attribute attribute
                      :attribute-type (str (type attribute))
                      :field-schema field-schema
                      :field-schema-type (str (type field-schema))
                      :field-schema-exists? (boolean field-schema)
                      :resolved-data-type data-type
                      :resolved-data-type-type (str (type data-type))
                      :blocked-by-field? blocked-by-field?
                      :allowed-by-type? allowed-by-type?
                      :reason reason
                      :draggable? draggable?})
     {:group-by-field group-by-field
      :group-by-field-pr (pr-str group-by-field)
      :group-by-field-type (str (type group-by-field))
      :entity-type entity-type
      :pipeline-id pipeline-id
      :attribute attribute
      :attribute-type (str (type attribute))
      :field-schema-exists? (boolean field-schema)
      :data-type data-type
      :blocked-by-field? blocked-by-field?
      :allowed-by-type? allowed-by-type?
      :reason (if row-group-by-field
                :row-grouping-active
                reason)
      :draggable? (boolean (and (nil? row-group-by-field)
                                (some? group-by-field)
                                draggable?))})))

(rf/reg-sub
 ::grouping-draggable?
 (fn [[_ kanban-id] _]
   (rf/subscribe [::grouping-draggable-debug kanban-id]))
 (fn [debug [_ _kanban-id]]
   (:draggable? debug)))

(rf/reg-sub
 ::card-config
 (<-1 ::compiled-config)
 (fn [compiled-config _]
   (get compiled-config :card-config)))

(rf/reg-sub
 ::columns
 (<-1 ::kanban-state)
 (fn [kanban-state _]
   (get kanban-state :columns)))

(rf/reg-sub
 ::columns-status
 (<-1 ::columns)
 (fn [columns _]
   (get columns :status :idle)))

(rf/reg-sub
 ::column-order
 (<-1 ::columns)
 (fn [columns _]
   (get columns :order)))

(rf/reg-sub
 ::column-by-value
 (<-1 ::columns)
 (fn [columns [_ _kanban-id]]
   (get-in columns [:by-value])))

(rf/reg-sub
 ::row-groups
 (<-1 ::kanban-state)
 (fn [kanban-state _]
   (get kanban-state model/row-groups-key)))

(rf/reg-sub
 ::row-groups-status
 (<-1 ::row-groups)
 (fn [row-groups _]
   (get row-groups :status :idle)))

(rf/reg-sub
 ::row-group-order
 (<-1 ::row-groups)
 (fn [row-groups _]
   (get row-groups :order)))

(rf/reg-sub
 ::row-group-by-value
 (<-1 ::row-groups)
 (fn [row-groups [_ _kanban-id]]
   (get-in row-groups [:by-value])))

(rf/reg-sub
 ::cards-by-column
 (<-1 ::kanban-state)
 (fn [kanban-state _]
   (get kanban-state model/cards-by-column-key)))

(rf/reg-sub
 ::cards-in-column
 (<-1 ::cards-by-column)
 (fn [cards-by-column [_ _kanban-id card-key]]
   (get-in cards-by-column [card-key :items] [])))

(rf/reg-sub
 ::column-state
 (<-1 ::cards-by-column)
 (fn [cards-by-column [_ _kanban-id card-key]]
   (get cards-by-column card-key {:status :idle
                                  :error nil
                                  :items []
                                  :next-cursor nil
                                  :total nil
                                  :has-more? true
                                  :rev 0})))

(rf/reg-sub
 ::column-status
 (<-1 ::column-state)
 (fn [column-state _]
   (get column-state :status)))

(rf/reg-sub
 ::column-error
 (<-1 ::column-state)
 (fn [column-state _]
   (get column-state :error)))

(rf/reg-sub
 ::column-next-cursor
 (<-1 ::column-state)
 (fn [column-state _]
   (get column-state :next-cursor)))

(rf/reg-sub
 ::column-total
 (<-1 ::column-state)
 (fn [column-state _]
   (get column-state :total)))

(rf/reg-sub
 ::column-has-more?
 (<-1 ::column-state)
 (fn [column-state _]
   (get column-state :has-more?)))
